import type { ApolloError } from "@apollo/client"
import React, {
    useContext,
    createContext,
    type PropsWithChildren,
    useEffect,
    useState,
} from "react"
import { pick } from "radash"

import type { MinimalOutfitterFieldsFragment } from "~graphql/outfitters.generated"
import { usePersistedPortalEntities } from "~utils/hooks/use-persisted-portal-entities"
import { useGlobalOutfitterFromParam } from "~utils/hooks/use-global-outfitter-from-param"
import usePortalEntities from "~utils/hooks/use-portal-entities"
import useGlobalEventOrganizerFromParam from "~utils/hooks/use-global-event-organizer-from-param"
import type {
    EventOrganizerFieldsFragment,
    EventOrganizerTeamMemberFieldsFragment,
} from "~graphql/event-organizers.generated"
import { PortalType, TeamMemberRole } from "~graphql/generated/types"
import { getGlobalPortalTypeQueryParam } from "~utils/helpers/url-param-helpers"
import { useUser } from "~contexts/user-provider"
import type { TeamMemberFieldsFragment } from "~graphql/team-members.generated"

let didRefreshSelectedOutfiter = false
let didRefreshSelectedEventOrganizer = false

export type PortalTypeForContext = PortalType | undefined | null
export type OutfitterTypeForContext =
    | MinimalOutfitterFieldsFragment
    | undefined
    | null
export type EventOrganizerTypeForContext =
    | EventOrganizerFieldsFragment
    | undefined
    | null

type PortalEntitiesContextType = {
    portalType: PortalTypeForContext
    setPortalType: (portalType: PortalTypeForContext) => void
    portalTypes: PortalType[]
    outfitter: OutfitterTypeForContext
    outfitterId: string | undefined | null
    setOutfitter: (outfitter: OutfitterTypeForContext) => void
    setOutfitterById: (id: string | undefined | null) => void
    outfitters: MinimalOutfitterFieldsFragment[]
    eventOrganizer: EventOrganizerTypeForContext
    eventOrganizerId: string | undefined | null
    setEventOrganizer: (eventOrganizer: EventOrganizerTypeForContext) => void
    setEventOrganizerById: (id: string | undefined | null) => void
    eventOrganizers: EventOrganizerFieldsFragment[]
    adminSearchText: string
    setAdminSearchText: (adminSearchText: string) => void
    isPortalEntityOwner: boolean
    isLoading: boolean
    error?: ApolloError | Error | null
}

const PortalEntitiesContext = createContext<PortalEntitiesContextType>({
    portalType: undefined,
    setPortalType: () => {},
    portalTypes: [],
    outfitter: undefined,
    outfitterId: undefined,
    setOutfitter: () => {},
    setOutfitterById: () => {},
    outfitters: [],
    eventOrganizer: undefined,
    eventOrganizerId: undefined,
    setEventOrganizer: () => {},
    setEventOrganizerById: () => {},
    eventOrganizers: [],
    adminSearchText: "",
    setAdminSearchText: () => {},
    isPortalEntityOwner: false,
    isLoading: false,
    error: null,
})

export function PortalEntitiesProvider({ children }: PropsWithChildren) {
    const contextValue = useGetPortalEntitiesValues()

    return (
        <PortalEntitiesContext.Provider value={contextValue}>
            {children}
        </PortalEntitiesContext.Provider>
    )
}

// Exported for testing.
export function useGetPortalEntitiesValues(): PortalEntitiesContextType {
    const [adminSearchText, setAdminSearchText] = useState("")
    const {
        portalType,
        setPortalType,
        outfitter,
        setOutfitter,
        eventOrganizer,
        setEventOrganizer,
    } = usePersistedPortalEntities()
    const {
        portalTypes,
        outfitters,
        eventOrganizers,
        defaultPortalType,
        defaultOutfitter,
        defaultEventOrganizer,
        isLoading,
        error,
    } = usePortalEntities({ adminSearchText, portalType })

    const isPortalEntityOwner = useIsPortalEntityOwner({
        portalType,
        outfitter,
        eventOrganizer,
    })
    const { outfitterFromQueryParam } = useGlobalOutfitterFromParam()
    const { eventOrganizerFromQueryParam } = useGlobalEventOrganizerFromParam()

    const setOutfitterById = useSetEntityByIdFunction({
        entities: outfitters,
        setEntity: setOutfitter,
    })
    const setEventOrganizerById = useSetEntityByIdFunction({
        entities: eventOrganizers,
        setEntity: setEventOrganizer,
    })

    useAutoSelectPortalType({ portalType, defaultPortalType, setPortalType })
    useAutoSelectAndRefreshPersistedEntity<MinimalOutfitterFieldsFragment>({
        entityFromQueryParam: outfitterFromQueryParam,
        entity: outfitter,
        defaultEntity: defaultOutfitter,
        entities: outfitters,
        setEntity: setOutfitter,
        didRefreshSelectedEntity: didRefreshSelectedOutfiter,
        updateDidRefreshSelectedEntity: setRefreshSelectedOutfitter,
    })
    useAutoSelectAndRefreshPersistedEntity<EventOrganizerFieldsFragment>({
        entityFromQueryParam: eventOrganizerFromQueryParam,
        entity: eventOrganizer,
        defaultEntity: defaultEventOrganizer,
        entities: eventOrganizers,
        setEntity: setEventOrganizer,
        didRefreshSelectedEntity: didRefreshSelectedEventOrganizer,
        updateDidRefreshSelectedEntity: setRefreshSelectedEventOrganizer,
    })

    return {
        portalType,
        setPortalType,
        portalTypes,
        outfitter,
        outfitterId: outfitter?.id,
        setOutfitter,
        setOutfitterById,
        outfitters,
        eventOrganizer,
        eventOrganizerId: eventOrganizer?.id,
        setEventOrganizer,
        setEventOrganizerById,
        eventOrganizers,
        adminSearchText,
        setAdminSearchText,
        isPortalEntityOwner,
        isLoading,
        error,
    }
}

export const usePortalEntitiesContext = () => useContext(PortalEntitiesContext)

export const useOutfitterContext = () => {
    const entitiesContext = usePortalEntitiesContext()

    return pick(entitiesContext, [
        "outfitter",
        "outfitterId",
        "setOutfitter",
        "setOutfitterById",
        "outfitters",
        "isLoading",
        "error",
    ])
}

export const useEventOrganizerContext = () => {
    const entitiesContext = usePortalEntitiesContext()

    return pick(entitiesContext, [
        "eventOrganizer",
        "eventOrganizerId",
        "setEventOrganizer",
        "setEventOrganizerById",
        "eventOrganizers",
        "isLoading",
        "error",
    ])
}

// Exported for tests
export function refreshLocalStorageEntityIfNeeded<
    Entity extends { id: string; updated_at: number },
>({
    persistedEntity,
    entities,
    onUpdate,
    didRefreshSelectedEntity,
    updateDidRefreshSelectedEntity,
}: {
    persistedEntity: Entity | null | undefined
    entities: Entity[]
    onUpdate: (entity: Entity | null | undefined) => void
    didRefreshSelectedEntity: boolean
    updateDidRefreshSelectedEntity: (didRefreshSelectedEntity: boolean) => void
}) {
    const isItTimeToRefresh =
        persistedEntity && entities.length > 0 && !didRefreshSelectedEntity
    if (!isItTimeToRefresh) return

    // Only allow refreshing to happen once to avoid unnecessary renders
    updateDidRefreshSelectedEntity(true)

    // If it is time, check if data updated recently
    const foundEntity = findEntityById(persistedEntity.id, entities)
    if (!foundEntity) return

    const wasEntityUpdated = foundEntity.updated_at > persistedEntity.updated_at

    // Do nothing if it was not updated
    if (!wasEntityUpdated) return

    onUpdate(foundEntity)
}

// For tests
export function clearDidRefreshSelectedOutfiter() {
    didRefreshSelectedOutfiter = false
}
export function clearDidRefreshSelectedEventOrganizer() {
    didRefreshSelectedEventOrganizer = false
}

function setRefreshSelectedOutfitter(newValue: boolean) {
    didRefreshSelectedOutfiter = newValue
}

function setRefreshSelectedEventOrganizer(newValue: boolean) {
    didRefreshSelectedEventOrganizer = newValue
}

function findEntityById<Entity extends { id: string }>(
    id: string | null | undefined,
    entities: Entity[]
) {
    if (!id) return null

    return entities.find((entity) => entity.id === id)
}

function useAutoSelectAndRefreshPersistedEntity<
    Entity extends { id: string; updated_at: number },
>({
    entityFromQueryParam,
    entity,
    defaultEntity,
    entities,
    setEntity,
    didRefreshSelectedEntity,
    updateDidRefreshSelectedEntity,
}: {
    entityFromQueryParam: Entity | undefined | null
    entity: Entity | undefined | null
    defaultEntity: Entity | undefined | null
    entities: Entity[]
    setEntity: (entity: Entity | null | undefined) => void
    didRefreshSelectedEntity: boolean
    updateDidRefreshSelectedEntity: (didRefreshSelectedEntity: boolean) => void
}) {
    const { isAdminNotImpersonating } = useUser()

    useEffect(() => {
        // If we have an entity from a query param, select it.
        if (entityFromQueryParam && entityFromQueryParam.id !== entity?.id) {
            setEntity(entityFromQueryParam)
            return
        }

        // If there's no entity selected, select the default entity.
        // If it's an admin and not impersonating, don't select the default entity.
        // Admin have the option of viewing all.
        if (!entity && defaultEntity && !isAdminNotImpersonating) {
            setEntity(defaultEntity)
            return
        }

        // If we have a selected entity, refresh the local storage entity if needed.
        if (entity) {
            refreshLocalStorageEntityIfNeeded({
                persistedEntity: entity,
                entities,
                onUpdate: setEntity,
                didRefreshSelectedEntity,
                updateDidRefreshSelectedEntity,
            })
        }
    }, [entity, entities.length, defaultEntity, entityFromQueryParam])
}

function useSetEntityByIdFunction<Entity extends { id: string }>({
    entities,
    setEntity,
}: {
    entities: Entity[]
    setEntity: (entity: Entity | null | undefined) => void
}) {
    return (entityId: string | null | undefined) => {
        if (!entityId) {
            setEntity(null)
            return
        }

        setEntity(findEntityById(entityId, entities))
    }
}

function useAutoSelectPortalType({
    portalType,
    defaultPortalType,
    setPortalType,
}: {
    portalType: PortalTypeForContext
    defaultPortalType: PortalTypeForContext
    setPortalType: (portalType: PortalTypeForContext) => void
}) {
    const portalTypeFromQueryParam = getGlobalPortalTypeQueryParam()
    const isParamPortalTypeValid = isQueryParamPortalTypeValid(
        portalTypeFromQueryParam
    )

    useEffect(() => {
        if (isParamPortalTypeValid && portalTypeFromQueryParam !== portalType) {
            setPortalType(portalTypeFromQueryParam)
            return
        }

        if (!portalType && defaultPortalType) setPortalType(defaultPortalType)
    }, [portalType, defaultPortalType])
}

function isQueryParamPortalTypeValid(
    portalType: string | null
): portalType is PortalType {
    if (!portalType) return false

    return Object.values<string>(PortalType).includes(portalType)
}

function useIsPortalEntityOwner({
    portalType,
    outfitter,
    eventOrganizer,
}: {
    portalType: PortalType | null | undefined
    outfitter: MinimalOutfitterFieldsFragment | null | undefined
    eventOrganizer: EventOrganizerFieldsFragment | null | undefined
}) {
    const isOutfitterOwner = useDoesUserHaveOwnerPermission(outfitter?.my_roles)
    const isEventOrganizerOwner = useDoesUserHaveOwnerPermission(
        eventOrganizer?.my_roles
    )

    switch (portalType) {
        case PortalType.Outfitter:
            return isOutfitterOwner
        case PortalType.RegionalDirector:
            return isEventOrganizerOwner
        default:
            return false
    }
}

function useDoesUserHaveOwnerPermission(
    userRoles:
        | TeamMemberFieldsFragment[]
        | EventOrganizerTeamMemberFieldsFragment[]
        | null
        | undefined
) {
    const { user, isAdminNotImpersonating } = useUser()

    // If an admin is impersonating, treat them like that user
    // so that they have a realistic experience with permissions.
    if (isAdminNotImpersonating) return true
    if (!userRoles || !user) return false

    return userRoles.some((myRole) => {
        const ownerRoles = [TeamMemberRole.CoOwner, TeamMemberRole.Owner]

        return ownerRoles.includes(myRole.role)
    })
}
